import type { InboxGroupDto, InboxRowDto } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [C3c §2] THE To Assign LIST AS A TWO-LEVEL TREE — and this REPLACES `lib/inboxGrouping.ts`.
 *
 *  ⚠️ THAT FILE REFUSED NESTING FOR FOUR REASONS. Three survive and are answered here; the
 *  fourth was correct given the list it was written for, and §2d removed its premise.
 *
 *    1. "Nesting inserts a band ROW, and a band row is not a share." — Answered by `rowKind`
 *       being an explicit discriminant on the wire AND on the row type, so nothing can read a
 *       header as a share; and by §2d, which makes the header a PAGE UNIT rather than chrome.
 *    2. "`arrangementGroups.ts`' nesting is for arrangement GROUPS, a different entity." — Still
 *       true, which is exactly why this is its own module rather than a second consumer of that.
 *    3. "A header claiming N members overstates a run a page boundary can break." — THE PREMISE
 *       IS GONE. That was a CLIENT band over a SERVER page of duty rows, so a run could straddle.
 *       §2d moves the page boundary to the arrangement, so a group's children are complete by
 *       construction — and where the server caps them, `dutyRowCount` says so.
 *    4. "It changes nothing — no sort, no filter, no `total`, no pagination." — NOW FALSE, and
 *       deliberately: `total` counts arrangements. Recorded as a cost paid, not one avoided.
 *
 *  ⚠️ THE DISCRIMINANT IS `rowKind`, NEVER `kind`. `InboxRowDto`'s parent already carries a
 *  `kind` (`STANDARD | ROUND_THE_CLOCK`), and a discriminant of that name would be silently
 *  overwritten by the spread below — `arrangementGroups.ts` records finding exactly that bug.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export type InboxDutyRow = InboxRowDto & {
  rowKind: 'duty';
  key: string;
  treeDepth: 1;
  parentKey: string;
  isLastChild: boolean;
};

export type InboxGroupRow = Omit<InboxGroupDto, 'rows'> & {
  rowKind: 'group';
  key: string;
  treeDepth: 0;
  isLastChild: boolean;
  children: InboxDutyRow[];
};

export type InboxTreeRow = InboxGroupRow | InboxDutyRow;

/** The duty row's identity, and the one the fill screen is opened by. */
export const dutyRowKey = (row: Pick<InboxRowDto, 'deploymentId' | 'divisionOrgUnitId'>): string =>
  `${row.deploymentId}:${row.divisionOrgUnitId}`;

/**
 * ⚠️ ASSEMBLED INSIDE `queryFn`, so `useServerTable` is instantiated on the RENDERED row type and
 * `tableProps` spreads with no cast — `pages/Arrangements`' own sentence, and what keeps
 * `pagination.total` the server's arrangement count rather than a length taken here.
 */
export const inboxTreeRows = (groups: readonly InboxGroupDto[]): InboxTreeRow[] =>
  groups.map(({ rows, ...group }, index) => {
    const key = `a:${group.arrangementId}`;
    return {
      ...group,
      rowKind: 'group',
      key,
      treeDepth: 0,
      isLastChild: index === groups.length - 1,
      children: rows.map((row, at) => ({
        ...row,
        rowKind: 'duty',
        key: dutyRowKey(row),
        treeDepth: 1,
        parentKey: key,
        isLastChild: at === rows.length - 1,
      })),
    };
  });
